import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, FileText, Loader2, ShieldCheck, Check, Lock, Maximize2, X, ImagePlus, Trash2 } from "lucide-react";
import Cropper from "react-easy-crop";
import { CvShell } from "@/components/cv/CvShell";
import { CvGate } from "@/components/cv/CvGate";
import { useAuth } from "@/context/AuthContext";
import { cvApi } from "@/lib/api";

const ACCENTS = [
  { key: "", label: "Domyślny", color: "#334155" },
  { key: "#1d4ed8", label: "Niebieski", color: "#1d4ed8" },
  { key: "#0f766e", label: "Morski", color: "#0f766e" },
  { key: "#7c3aed", label: "Fioletowy", color: "#7c3aed" },
  { key: "#b45309", label: "Bursztyn", color: "#b45309" },
  { key: "#111827", label: "Grafit", color: "#111827" },
];

const A4_W = 794; // 210mm @ 96dpi
const A4_H = 1123; // 297mm @ 96dpi
const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const ZOOMS = [
  { key: "fit", label: "Dopasuj" },
  { key: 0.75, label: "75%" },
  { key: 1, label: "100%" },
];

// Kadrowanie do kwadratu (canvas) — pewny efekt 1:1 w podglądzie i PDF.
function getCroppedBlob(src, area) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const size = Math.round(Math.min(area.width, area.height));
      const out = Math.min(700, size) || 700;
      const canvas = document.createElement("canvas");
      canvas.width = out;
      canvas.height = out;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, out, out);
      ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, out, out);
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("crop failed"))), "image/jpeg", 0.9);
    };
    img.onerror = reject;
    img.src = src;
  });
}


export default function CvExport() {
  const { cvId } = useParams();
  const { user } = useAuth();
  const [cv, setCv] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [error, setError] = useState("");
  const [template, setTemplate] = useState("klasyczny");
  const [accent, setAccent] = useState("");
  const [nonce, setNonce] = useState(0);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [zoom, setZoom] = useState("fit");
  const [fullscreen, setFullscreen] = useState(false);
  const [hasPhoto, setHasPhoto] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileRef = useRef(null);
  const [cropSrc, setCropSrc] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [cropZoom, setCropZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  useEffect(() => {
    if (!user) return;
    cvApi.get(cvId).then((d) => { setCv(d); setTemplate(d.template || "klasyczny"); setAccent(d.accent || ""); setHasPhoto(Boolean(d.photo_path)); }).catch(() => setError("Nie udało się wczytać CV."));
    cvApi.templates().then((d) => setTemplates(d.templates || [])).catch(() => {});
  }, [cvId, user]);

  const templatePhoto = templates.find((t) => t.key === template)?.photo || false;

  const onPickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Dozwolone formaty: JPG, PNG, WEBP."); return;
    }
    if (file.size > 5 * 1024 * 1024) { toast.error("Zdjęcie jest za duże (maks. 5 MB)."); return; }
    setCrop({ x: 0, y: 0 });
    setCropZoom(1);
    setCroppedAreaPixels(null);
    setCropSrc(URL.createObjectURL(file));
  };

  const confirmCrop = async () => {
    if (!cropSrc || !croppedAreaPixels) return;
    setUploadingPhoto(true);
    try {
      const blob = await getCroppedBlob(cropSrc, croppedAreaPixels);
      const file = new File([blob], "zdjecie.jpg", { type: "image/jpeg" });
      await cvApi.uploadPhoto(cvId, file);
      setHasPhoto(true);
      setNonce((n) => n + 1);
      URL.revokeObjectURL(cropSrc);
      setCropSrc("");
      toast.success("Zdjęcie zapisane.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Nie udało się przesłać zdjęcia.";
      toast.error(typeof msg === "string" ? msg : "Nie udało się przesłać zdjęcia.");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = async () => {
    setUploadingPhoto(true);
    try {
      await cvApi.deletePhoto(cvId);
      setHasPhoto(false);
      setNonce((n) => n + 1);
      toast.success("Zdjęcie usunięte.");
    } catch {
      toast.error("Nie udało się usunąć zdjęcia.");
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Podgląd = ten sam endpoint co eksport (iframe src → fonty i polskie znaki 1:1 z PDF).
  const previewUrl = useMemo(() => {
    const p = new URLSearchParams({ template });
    if (accent) p.set("accent", accent);
    p.set("_", String(nonce));
    return `${BASE}/cv/${cvId}/render?${p.toString()}`;
  }, [cvId, template, accent, nonce]);

  useEffect(() => { setLoadingPreview(true); }, [previewUrl]);

  const chooseTemplate = async (key, available) => {
    if (!available) return;
    setTemplate(key);
    setConfirmed(false);
    setNonce((n) => n + 1);
    try { await cvApi.patch(cvId, { template: key }); } catch { /* autosave best-effort */ }
    toast.success("Szablon wybrany. Dane pozostają bez zmian.");
  };

  const chooseAccent = async (key) => {
    setAccent(key);
    setNonce((n) => n + 1);
    try { await cvApi.patch(cvId, { accent: key }); } catch { /* ignore */ }
  };

  const download = async (fmt) => {
    if (!confirmed) return;
    setDownloading(fmt);
    try {
      const params = { template };
      if (accent) params.accent = accent;
      const blob = await cvApi.exportBlob(cvId, fmt, params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `CV.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Pobrano plik ${fmt.toUpperCase()}.`);
    } catch {
      toast.error("Nie udało się pobrać pliku. Spróbuj ponownie.");
    } finally {
      setDownloading("");
    }
  };

  const scale = zoom === "fit" ? 0.62 : zoom;

  return (
    <CvShell active="szablony">
      <div data-testid="cv-export" className="pb-28">
        <h1 className="font-display text-2xl font-semibold text-brand-text sm:text-3xl">Wybór szablonu i pobieranie</h1>
        <p className="mt-2 max-w-2xl text-brand-sec">Wybierz szablon, sprawdź podgląd A4 i pobierz plik. Szablon, kolor i układ możesz zmienić później — dane pozostają bez zmian.</p>

        {!user ? (
          <div className="mt-8 max-w-2xl"><CvGate returnTo={`/cv/szablon/${cvId}`} message="Załóż bezpłatne konto, aby pobrać swoje CV." /></div>
        ) : error ? (
          <div className="mt-8 rounded-panel border border-red-500/40 bg-red-500/10 p-6 text-sm text-red-300">{error}</div>
        ) : !cv ? (
          <div className="mt-10 flex justify-center"><Loader2 className="animate-spin text-brand-accent" /></div>
        ) : (
          <div className="mt-6 grid gap-6 lg:grid-cols-[380px_1fr]">
            {/* Panel: galeria + akcje */}
            <div className="space-y-5">
              <Link to={`/cv/kreator/${cvId}`} data-testid="cv-export-back" className="inline-flex items-center gap-2 rounded-button border border-brand-line px-4 py-2.5 text-sm font-semibold text-brand-text hover:border-brand-accent/60">
                <ArrowLeft size={15} /> Wróć do edycji wywiadu
              </Link>

              {/* Galeria szablonów (CV-A03) */}
              <div>
                <p className="mb-2 text-sm font-semibold text-brand-text">Szablony</p>
                <div className="grid grid-cols-2 gap-3" data-testid="cv-template-gallery">
                  {templates.map((t) => {
                    const on = template === t.key;
                    return (
                      <div key={t.key} data-testid={`cv-template-card-${t.key}`} className={`relative rounded-panel border p-3 ${on ? "border-brand-accent ring-1 ring-brand-accent" : "border-brand-line"} ${t.available ? "" : "opacity-70"}`}>
                        <div className="mb-2 flex aspect-[3/4] items-center justify-center overflow-hidden rounded-card border border-brand-line bg-white">
                          <MiniThumb t={t} />
                        </div>
                        <div className="flex items-center justify-between gap-1">
                          <span className="truncate text-[13px] font-medium text-brand-text">{t.label}</span>
                          {t.ats && <span className="rounded-full bg-brand-accent/15 px-1.5 py-0.5 text-[10px] font-semibold text-brand-accent">ATS</span>}
                        </div>
                        {t.available ? (
                          <button type="button" data-testid={`cv-template-choose-${t.key}`} onClick={() => chooseTemplate(t.key, true)} className={`mt-2 w-full rounded-button px-2 py-1.5 text-[12px] font-semibold ${on ? "bg-brand-accent text-brand-on" : "border border-brand-line text-brand-text hover:border-brand-accent/60"}`}>
                            {on ? (<span className="inline-flex items-center gap-1"><Check size={12} /> Wybrany</span>) : "Wybieram"}
                          </button>
                        ) : (
                          <div className="mt-2 flex w-full items-center justify-center gap-1 rounded-button border border-dashed border-brand-line px-2 py-1.5 text-[12px] text-brand-muted"><Lock size={12} /> Wkrótce</div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="mt-2 text-[12px] text-brand-muted">Dostępne szablony: Klasyczny, Minimalistyczny, Techniczny (ATS) oraz Nowoczesny, Premium i Kreatywny (dwukolumnowe). Zdjęcie dodasz w szablonach Premium i Kreatywny.</p>
              </div>

              {/* Zdjęcie (tylko szablony ze zdjęciem: Premium, Kreatywny) */}
              {templatePhoto && (
                <div data-testid="cv-photo-section">
                  <p className="mb-2 text-sm font-semibold text-brand-text">Zdjęcie do CV</p>
                  <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onPickPhoto} className="hidden" data-testid="cv-photo-input" />
                  <div className="flex flex-wrap items-center gap-2">
                    <button type="button" data-testid="cv-photo-upload" disabled={uploadingPhoto} onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 rounded-button border border-brand-line px-4 py-2.5 text-sm font-semibold text-brand-text hover:border-brand-accent/60 disabled:opacity-50">
                      {uploadingPhoto ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />} {hasPhoto ? "Zmień zdjęcie" : "Dodaj zdjęcie"}
                    </button>
                    {hasPhoto && (
                      <button type="button" data-testid="cv-photo-remove" disabled={uploadingPhoto} onClick={removePhoto} className="inline-flex items-center gap-1.5 rounded-button border border-brand-line px-3 py-2.5 text-sm font-semibold text-red-400 hover:border-red-400/60 disabled:opacity-50">
                        <Trash2 size={15} /> Usuń
                      </button>
                    )}
                  </div>
                  <p className="mt-1.5 text-[12px] text-brand-muted">JPG, PNG lub WEBP, do 5 MB. Bez zdjęcia pokażemy awatar z inicjałami.</p>
                </div>
              )}

              {/* Kolor akcentu */}
              <div>
                <p className="mb-2 text-sm font-semibold text-brand-text">Kolor akcentu</p>
                <div className="flex flex-wrap gap-2" data-testid="cv-accent-picker">
                  {ACCENTS.map((a) => (
                    <button key={a.key || "default"} type="button" data-testid={`cv-accent-${a.key || "default"}`} onClick={() => chooseAccent(a.key)} title={a.label} className={`h-8 w-8 rounded-full border-2 ${accent === a.key ? "border-brand-text" : "border-transparent"}`} style={{ background: a.color }} />
                  ))}
                </div>
              </div>

              {/* Akceptacja + eksport */}
              <label className="flex cursor-pointer items-start gap-2.5 rounded-panel border border-brand-line bg-brand-surface p-4">
                <input type="checkbox" data-testid="cv-export-confirm" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[color:var(--brand-accent,#b6f500)]" />
                <span className="text-[13px] text-brand-text">Zatwierdzam podgląd — dane są poprawne i zgodne z moim rzeczywistym doświadczeniem.</span>
              </label>
              <div className="grid gap-2">
                <button type="button" data-testid="cv-export-pdf" disabled={!confirmed || downloading} onClick={() => download("pdf")} className="inline-flex items-center justify-center gap-2 rounded-button bg-brand-accent px-5 py-2.5 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-40">
                  {downloading === "pdf" ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Pobierz PDF
                </button>
                <button type="button" data-testid="cv-export-docx" disabled={!confirmed || downloading} onClick={() => download("docx")} className="inline-flex items-center justify-center gap-2 rounded-button border border-brand-line px-5 py-2.5 text-sm font-semibold text-brand-text transition-colors hover:border-brand-accent/60 disabled:cursor-not-allowed disabled:opacity-40">
                  {downloading === "docx" ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />} Pobierz DOCX
                </button>
              </div>
              <p className="flex items-start gap-2 text-[12.5px] text-brand-muted"><ShieldCheck size={14} className="mt-0.5 shrink-0 text-brand-accent" /> W eksporcie znajdą się wyłącznie wypełnione sekcje. Nie dodajemy przykładowych ani zmyślonych danych.</p>
            </div>

            {/* Podgląd A4 — ten sam HTML co eksport (1:1) */}
            <div>
              {/* Pasek narzędzi podglądu */}
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-semibold text-brand-text">Podgląd A4</span>
                <div className="flex items-center gap-1.5" data-testid="cv-preview-toolbar">
                  <div className="flex overflow-hidden rounded-button border border-brand-line">
                    {ZOOMS.map((z) => (
                      <button key={String(z.key)} type="button" data-testid={`cv-zoom-${z.key}`} onClick={() => setZoom(z.key)} className={`px-2.5 py-1.5 text-[12px] font-semibold ${zoom === z.key ? "bg-brand-accent text-brand-on" : "text-brand-text hover:bg-brand-surface"}`}>{z.label}</button>
                    ))}
                  </div>
                  <button type="button" data-testid="cv-preview-fullscreen" onClick={() => setFullscreen(true)} className="inline-flex items-center gap-1 rounded-button border border-brand-line px-2.5 py-1.5 text-[12px] font-semibold text-brand-text hover:border-brand-accent/60">
                    <Maximize2 size={13} /> Powiększ podgląd
                  </button>
                </div>
              </div>

              <div data-testid="cv-export-preview" className="relative overflow-auto rounded-panel border border-brand-line bg-[#525659] p-4" style={{ maxHeight: "78vh" }}>
                {loadingPreview && <div className="absolute right-4 top-4 z-10 inline-flex items-center gap-1.5 rounded-full bg-black/50 px-2.5 py-1 text-[11px] text-white"><Loader2 size={12} className="animate-spin" /> Odświeżanie…</div>}
                <div className="mx-auto shadow-xl" style={{ width: A4_W * scale, height: A4_H * scale }}>
                  <iframe
                    key={previewUrl}
                    data-testid="cv-a4-iframe"
                    title="Podgląd CV A4"
                    src={previewUrl}
                    onLoad={() => setLoadingPreview(false)}
                    style={{ width: A4_W, height: A4_H, border: "none", background: "white", display: "block", transform: `scale(${scale})`, transformOrigin: "top left" }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pełnoekranowy podgląd A4 */}
      {fullscreen && (
        <div data-testid="cv-fullscreen-overlay" className="fixed inset-0 z-[60] flex flex-col bg-black/80 backdrop-blur-sm" onClick={() => setFullscreen(false)}>
          <div className="flex items-center justify-between px-5 py-3 text-white">
            <span className="text-sm font-semibold">Podgląd A4 — pełny rozmiar</span>
            <button type="button" data-testid="cv-fullscreen-close" onClick={() => setFullscreen(false)} className="inline-flex items-center gap-1.5 rounded-button border border-white/30 px-3 py-1.5 text-[13px] font-semibold hover:bg-white/10">
              <X size={15} /> Zamknij
            </button>
          </div>
          <div className="flex-1 overflow-auto px-4 pb-8" onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto shadow-2xl" style={{ width: A4_W }}>
              <iframe
                key={`fs-${previewUrl}`}
                data-testid="cv-fullscreen-iframe"
                title="Podgląd CV A4 pełny"
                src={previewUrl}
                style={{ width: A4_W, minHeight: A4_H, border: "none", background: "white", display: "block" }}
              />
            </div>
          </div>
        </div>
      )}
      {/* Modal kadrowania zdjęcia */}
      {cropSrc && (
        <div data-testid="cv-crop-overlay" className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-panel border border-brand-line bg-brand-bg p-5">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm font-semibold text-brand-text">Wykadruj zdjęcie</p>
              <button type="button" data-testid="cv-crop-cancel" onClick={() => { URL.revokeObjectURL(cropSrc); setCropSrc(""); }} className="rounded-button border border-brand-line p-1.5 text-brand-text hover:border-brand-accent/60"><X size={15} /></button>
            </div>
            <div className="relative h-64 w-full overflow-hidden rounded-card bg-[#333]">
              <Cropper
                image={cropSrc}
                crop={crop}
                zoom={cropZoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setCropZoom}
                onCropComplete={(_, px) => setCroppedAreaPixels(px)}
              />
            </div>
            <div className="mt-4 flex items-center gap-3">
              <span className="text-[12px] text-brand-muted">Powiększ</span>
              <input data-testid="cv-crop-zoom" type="range" min={1} max={3} step={0.01} value={cropZoom} onChange={(e) => setCropZoom(Number(e.target.value))} className="flex-1 accent-[color:var(--brand-accent,#b6f500)]" />
            </div>
            <p className="mt-2 text-[12px] text-brand-muted">Przeciągnij zdjęcie i użyj suwaka, aby wyśrodkować twarz w okręgu.</p>
            <div className="mt-4 flex gap-2">
              <button type="button" data-testid="cv-crop-save" disabled={uploadingPhoto || !croppedAreaPixels} onClick={confirmCrop} className="inline-flex flex-1 items-center justify-center gap-2 rounded-button bg-brand-accent px-4 py-2.5 text-sm font-semibold text-brand-on disabled:opacity-50">
                {uploadingPhoto ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Zapisz zdjęcie
              </button>
              <button type="button" onClick={() => { URL.revokeObjectURL(cropSrc); setCropSrc(""); }} className="rounded-button border border-brand-line px-4 py-2.5 text-sm font-semibold text-brand-text">Anuluj</button>
            </div>
          </div>
        </div>
      )}
    </CvShell>
  );
}

// Prosta miniatura układu (nie miniatura danych — szkic struktury szablonu).
const MiniThumb = ({ t }) => {
  const bar = (w) => <div style={{ height: 4, width: w, background: "#e5e7eb", borderRadius: 2 }} />;
  const accent = t.accent;
  return (
    <div className="flex h-full w-full flex-col gap-1 p-2" style={{ fontSize: 0 }}>
      <div style={{ height: 8, width: "60%", background: accent, borderRadius: 2 }} />
      {bar("40%")}
      <div className="my-0.5" style={{ height: 3, width: "30%", background: accent, borderRadius: 2 }} />
      {t.columns === 2 ? (
        <div className="flex flex-1 gap-1">
          <div className="flex flex-[2] flex-col gap-1">{bar("90%")}{bar("80%")}{bar("85%")}</div>
          <div className="flex flex-1 flex-col gap-1">{bar("100%")}{bar("70%")}</div>
        </div>
      ) : (
        <div className="flex flex-1 flex-col gap-1">{bar("95%")}{bar("88%")}{bar("92%")}{bar("70%")}</div>
      )}
    </div>
  );
};
